import { z } from "zod";
import { changeActorPath } from "./change-log";
import { bodyRegion } from "./injuries";
import { calendarDay } from "./profile";
/**
 * The workout session on the wire — starting one, logging sets into it,
 * correcting a set, ending it, and the two things the person is asked
 * afterwards (`D110`, `D100`).
 *
 * ── `load: null` IS BODYWEIGHT, AND IT IS NOT `0` ───────────────────────────
 * The aggregate is explicit about this (`record-workout-set.ts`) and so is
 * every mapping on the way here. A pull-up logged as `0 kg` is a claim that
 * the person moved nothing; `null` is the truth, and the difference is a
 * volume total that is either right or catastrophically wrong.
 *
 * ── A SET MAY BE RECORDED INTO AN ENDED SESSION ─────────────────────────────
 * `workout-session.ts:447-458` allows it deliberately — somebody who logged
 * the last set after tapping *end* is not an error to reject. Nothing in this
 * file forbids it either.
 */
/**
 * What a set WAS, beside what it measured (`D209`): a warm-up, a drop set, or
 * a set taken to failure. ABSENT or `null` is a working set — every set
 * logged before the field existed, and every set a client older than it
 * sends. A warm-up counts toward no total, estimate, record or progression
 * rule; a drop or failure set counts as a working one.
 */
export const SET_KINDS = ["warmup", "drop", "failure"];
export const setKind = z.enum(SET_KINDS);
/** A unilateral set logged per side (`D209`); absent or `null` is both. */
export const SET_SIDES = ["left", "right"];
export const setSide = z.enum(SET_SIDES);
/**
 * One logged set, as the session view and the history both carry it.
 *
 * The actor pair rides along (`D69`): a set logged by a trainer, or by the
 * assistant on the person's behalf, is a different fact from one the person
 * typed, and the session screen may say so.
 */
export const workoutSetView = z.object({
    actorAccount: z.string().min(1),
    actorPath: changeActorPath,
    exerciseId: z.string().min(1),
    /** All three nullable: the catalogue decides which are meaningful, and a
     *  plank has no reps. */
    load: z.number().nullable(),
    performedAt: z.iso.datetime(),
    reps: z.int().nullable(),
    rpe: z.number().nullable(),
    setId: z.string().min(1),
    /**
     * The two measures a rep count cannot carry (`D151`): a hold in seconds and
     * a carry or a run in metres. Nullable like `reps` — `null` is *"not
     * measured"* — and optional in the contract only, for the deploy window.
     */
    distanceMetres: z.number().nullable().optional(),
    durationSeconds: z.int().nullable().optional(),
    /** 1-based, within the session and per exercise. A correction supersedes a
     *  set and KEEPS its number — see `correctSetOutput`. */
    setNumber: z.int().positive(),
    /**
     * `D209`. Nullable and OPTIONAL, like every added output field (`D172`):
     * `null` is a working set from a server that knows the column, absent is a
     * server from before it. Both read as a working set on both sides.
     */
    kind: setKind.nullable().optional(),
    side: setSide.nullable().optional(),
});
/**
 * The two-tap check-in before a session (`D148`): how the person slept and
 * how stressed they are. Two words each, because the whole point is that it
 * costs nothing to answer — a scale would be skipped.
 */
export const READINESS_SLEEP = ["poor", "ok", "good"];
export const READINESS_STRESS = ["low", "moderate", "high"];
export const sessionReadiness = z.strictObject({
    sleep: z.enum(READINESS_SLEEP),
    stress: z.enum(READINESS_STRESS),
});
export const workoutSessionView = z.object({
    /** Present, and `true`, only when the session ended `abandoned` (`D177`):
     *  its sets are real, but it is not a workout. Absent for every other
     *  ending and for an open session — never `false`, as on
     *  {@link sessionSummary}. An app that predates it strips the key. */
    abandoned: z.literal(true).optional(),
    endedAt: z.iso.datetime().nullable(),
    /** Which of the plan's sessions this bout was (`D119`). `null` for a session
     *  started off-plan, which is a normal thing to do. */
    planSessionId: z.string().nullable(),
    /** `null` when the check-in was skipped, which it may be. */
    readiness: sessionReadiness.nullable().optional(),
    sessionId: z.string().min(1),
    /** In `set_number` order. Superseded and removed rows are already gone
     *  (`D53`) — this is the one read where they are NOT wanted, unlike the
     *  change feed, which exists to show them. */
    sets: z.array(workoutSetView),
    startedAt: z.iso.datetime(),
});
/**
 * `POST /me/sessions`.
 *
 * With `id`, the start is a REPLAYABLE write (`D19`): the offline outbox
 * resends it whenever it did not see the answer, and a replay of the same
 * `id` answers the stored session — its original `startedAt` and
 * adjustments, the same status — and opens nothing. An `id` another account
 * holds answers 404 `workout.session_not_found`.
 */
export const sessionStartInput = z.strictObject({
    /** A client-generated UUID (the app mints v7). Omitted = the server mints
     *  the id, and a second start is refused (409
     *  `workout.session_already_open`) while this one is open. */
    id: z.uuid().optional(),
    planSessionId: z.string().min(1).nullable().optional(),
    /** Omitted = the person skipped the check-in; the session runs as planned. */
    readiness: sessionReadiness.optional(),
    /** The client's clock at the start (`D20`), for a start replayed after the
     *  fact. Omitted = the server's now. Never in the future (422). */
    startedAt: z.iso.datetime().optional(),
});
/**
 * One item the engine trimmed for TODAY, on the strength of the readiness
 * answer (`D148`) — the plan itself is untouched. A key and not a sentence,
 * as every reason on this wire is (`D27`).
 *
 * `cutPct` and `rirIncrease` are the engine's own numbers, carried rather
 * than re-derived: the app used to hold a second copy of the two percentages
 * to fill `{pct}`, which drifts silently the day the engine's constants move.
 * Both are optional so an app built against this contract survives a server
 * older than the field, a rollback included (`D172`).
 */
export const sessionAdjustment = z.object({
    itemId: z.string().min(1),
    reasonKey: z.string().min(1),
    targetSets: z.int().positive(),
    /** The percentage cut that produced `targetSets` (`D148`) — the `{pct}` of
     *  the reason's copy. Absent from a server older than the field. */
    cutPct: z.int().min(1).max(100).optional(),
    /** Reps in reserve ADDED today on top of the plan's RIR; `0` = none.
     *  Absent from a server older than the field. */
    rirIncrease: z.int().min(0).optional(),
});
export const sessionStarted = z.object({
    /** Empty when nothing was trimmed, or the check-in was skipped. */
    adjustments: z.array(sessionAdjustment).optional(),
    sessionId: z.string().min(1),
    startedAt: z.iso.datetime(),
});
/**
 * `GET /me/sessions?from&to` — days, not instants.
 *
 * `z.coerce` is deliberately not used anywhere in this package (see
 * `change-log.ts`): it turns `""` into `0` and `"abc"` into `NaN`, and a range
 * that silently became one of those returns an empty history reading as the
 * sentence *"you have never trained"*.
 */
export const sessionListQuery = z.object({
    /** Inclusive. Omitted = from the first session ever recorded. */
    from: calendarDay.optional(),
    /** Inclusive. Omitted = up to the most recent. */
    to: calendarDay.optional(),
});
export const sessionListOutput = z.object({
    sessions: z.array(workoutSessionView),
});
/**
 * One set of a PAST session, as the history's correction list reads it
 * (`GET /me/sessions/:id/sets?locale=`, `D11`).
 *
 * ── IT IS `workoutSetView` PLUS A NAME, NOT A SECOND SET SHAPE ──────────────
 * Spread, never restated: a field the session view grows is a field this list
 * grows, and the app's `setDetail` reads one shape whether the set came from
 * the store or from here. `exerciseName` is the only addition, because a past
 * session is read outside the workout store — nothing there knows what
 * `ex-0142` is called, and a per-row catalogue fetch would be one request per
 * set.
 *
 * It is OPTIONAL, like every added output field (`D172`): a server that
 * predates the name join answers the row without it, and the list then labels
 * the row with its exercise id — which is truthful — rather than refusing the
 * whole page.
 */
export const sessionSetView = z.object({
    ...workoutSetView.shape,
    /** Localized by `?locale=`, English by default. Absent from a server older
     *  than the field; the id is then the honest label. */
    exerciseName: z.string().min(1).optional(),
});
/**
 * `GET /me/sessions/:id/sets` — the session's VISIBLE sets, oldest first.
 *
 * Oldest first and not in `set_number` order, which is what
 * `workoutSessionView.sets` carries: that number is minted when the server
 * writes the row, and the outbox replays a set with the client's own
 * `performedAt` (`D19`, `D20`) — so a set logged offline and resent later
 * carries a HIGHER number than sets performed after it. A list somebody
 * corrects a set from has to read in the order the sets happened.
 *
 * Superseded and removed rows are already gone (`D53`), so the row this list
 * shows is the row a `PUT /me/sets/:id` may still correct.
 */
export const sessionSetsOutput = z.object({
    sets: z.array(sessionSetView),
});
/** `POST /me/sessions/:id/sets`. */
export const recordSetInput = z.strictObject({
    /** Metres, for a carry or a run (`D151`). Optional AND nullable, like `load`. */
    distanceMetres: z.number().positive().nullable().optional(),
    /** Seconds held, for a plank or a hang (`D151`). */
    durationSeconds: z.int().positive().nullable().optional(),
    exerciseId: z.string().min(1),
    /**
     * The set's id, generated by the client (a UUIDv7) — the idempotency key of
     * the offline outbox (`D19`). A replay of an id already stored answers the
     * STORED row's `performedAt`, `setId` and `setNumber`, even when that row
     * was corrected or removed since, and writes nothing: no second row, no new
     * number, no session restamp. Omitted = the server mints one, and every
     * request is a new set (the agent's path).
     */
    id: z.uuid().optional(),
    /** Omitted = now, on the SERVER's clock. A client clock is the one thing a
     *  set's timestamp must not depend on. */
    performedAt: z.iso.datetime().optional(),
    load: z.number().nonnegative().nullable().optional(),
    reps: z.int().nonnegative().nullable().optional(),
    rpe: z.number().min(1).max(10).nullable().optional(),
    /**
     * `D209`. Omitted or `null` is a working set. A new INPUT field (`D172`):
     * an API older than it answers 422 `request.invalid_body` to a body that
     * names it, so the app sends it only once this server is deployed — and
     * sends it only when the set is not a working one, so a working set's body
     * is byte-for-byte what it was before.
     */
    kind: setKind.nullable().optional(),
    side: setSide.nullable().optional(),
});
export const setRecorded = z.object({
    performedAt: z.iso.datetime(),
    setId: z.string().min(1),
    setNumber: z.int().positive(),
});
/**
 * `PUT /me/sets/:id` — a correction.
 *
 * ── OMITTED IS CARRIED FORWARD; `null` IS CLEARED ──────────────────────────
 * The two are different and both are needed: a person fixing the reps must not
 * have to resend the load, and a person who logged `8 RPE` by accident must be
 * able to remove it. Every field is therefore `.optional()` AND, where the
 * column is nullable, `.nullable()`.
 *
 * `exerciseId` is optional but NOT nullable — a set is of an exercise, and a
 * correction that could clear it would leave a row nothing can read.
 */
export const correctSetInput = z.strictObject({
    distanceMetres: z.number().positive().nullable().optional(),
    durationSeconds: z.int().positive().nullable().optional(),
    exerciseId: z.string().min(1).optional(),
    /**
     * The REPLACEMENT row's id, generated by the client (`D19`). A replay whose
     * replacement id is already stored answers that stored replacement and
     * writes nothing. Omitted = the server mints one, and correcting a row that
     * is already superseded answers 404 `workout.set_not_found`, as before.
     */
    id: z.uuid().optional(),
    load: z.number().nonnegative().nullable().optional(),
    performedAt: z.iso.datetime().optional(),
    reps: z.int().nonnegative().nullable().optional(),
    rpe: z.number().min(1).max(10).nullable().optional(),
    /** `D209`, under the same rule as every field here: omitted carries the
     *  kind forward, `null` makes the set a working one again. */
    kind: setKind.nullable().optional(),
    side: setSide.nullable().optional(),
});
export const setCorrected = z.object({
    /** The NEW row. A correction is a new set plus a soft delete of the old one
     *  (`D53`), so that the change feed can show both. */
    setId: z.string().min(1),
    setNumber: z.int().positive(),
    /** The row this one replaced — already soft-deleted when this returns. */
    supersededSetId: z.string().min(1),
});
/** What the person reports about a set that hurt (`sessionFeedbackInput`). */
export const PAIN_TYPES = [
    "joint",
    "muscle_acute",
    "burn",
    "discomfort",
    "other",
];
export const painDuring = z.object({
    exerciseId: z.string().min(1),
    region: bodyRegion,
    type: z.enum(PAIN_TYPES),
});
/**
 * `PUT /me/sessions/:id/feedback` — the four questions after a session.
 *
 * This is the engine's feedback loop: `difficulty` and `durationFit` move the
 * next session's volume, `painDuring` triggers a substitution, and
 * `unavailableEquipmentIds` says the gym did not have what the plan assumed.
 * Every field is required — a partially answered form is one the app should
 * not send, because a missing `difficulty` and a `3` are indistinguishable to
 * everything downstream.
 */
export const sessionFeedbackInput = z.strictObject({
    difficulty: z.int().min(1).max(5),
    durationFit: z.enum(["ok", "too_long", "too_short"]),
    energyAfter: z.int().min(1).max(5),
    painDuring: z.array(painDuring),
    unavailableEquipmentIds: z.array(z.int().positive()),
});
export const SORENESS_LEVELS = ["none", "mild", "moderate", "intense"];
/**
 * `PUT /me/sessions/:id/soreness` — the answer to the next-morning check-in
 * push (`D100`).
 *
 * `regions` is optional because the check-in's one-tap answer is `overall`
 * alone; the detail is a second screen the person may skip.
 */
export const sorenessInput = z.strictObject({
    overall: z.enum(SORENESS_LEVELS),
    regions: z
        .array(z.object({
        intensity: z.int().min(1).max(5),
        region: bodyRegion,
    }))
        .optional(),
});
/**
 * What the summary push and the end-of-session screen both say.
 *
 * `previousVolumeKg` is `null` for a first session of that plan session, and
 * that is the honest answer — a `0` would render as *"you did infinitely more
 * than last time"*, which is `D104`'s progress-against-the-person turned into
 * a lie by a default.
 */
/**
 * One change to the next session, WITH its parameters — what
 * `nextSessionChanges` could not say (`D144`). *"Bench: +2.5 kg"* needs the
 * exercise and the number; a bare key rendered *"load increased"* for every
 * exercise at once.
 *
 * Named `sessionSummaryChange` and not `sessionChange`: the change feed
 * (`change-log.ts`) already exports a `sessionChange`, and the training barrel
 * cannot carry two.
 */
export const sessionSummaryChange = z.object({
    params: z.record(z.string(), z.union([z.string(), z.number()])),
    reasonKey: z.string().min(1),
});
export const personalRecord = z.object({
    exerciseId: z.string().min(1),
    /** `true` when no earlier estimate existed — the first logged lift, not a PR beaten. */
    firstEver: z.boolean(),
});
/**
 * A muscle the session worked, in FRACTIONAL sets (`D203`): 1 for each set
 * whose exercise targets it, 0.5 for each where it is a synergist, warm-ups 0.
 * The summary's body map lights these.
 */
export const summaryMuscle = z.object({
    muscleId: z.int().positive(),
    sets: z.number().nonnegative(),
});
/**
 * "vs o último treino que trabalhou os mesmos músculos" (`D204`): the most
 * recent earlier workout whose target muscles were the same as this one's.
 * Until `D255` it was sent only when the plan's own comparison
 * (`previousVolumeKg`) was absent; now it is sent beside it, and the app
 * folds the two when `sessionId` is `previousSession.sessionId`.
 */
export const summaryMatched = z.object({
    sessionId: z.string().min(1),
    startedAt: z.iso.datetime(),
    /** That session's working volume, warm-ups out. */
    volumeKg: z.number().nonnegative(),
    /** This session's, counted the same way. */
    currentVolumeKg: z.number().nonnegative(),
    /** The exercises trained in BOTH, with each one's best Epley estimate then
     *  and now; at most three, the heaviest now first. */
    lifts: z.array(z.object({
        exerciseId: z.string().min(1),
        e1rmKg: z.number().nonnegative(),
        previousE1rmKg: z.number().nonnegative(),
    })),
});
/**
 * How a session ended (`D42`): the whole of today's plan, less of it by the
 * person's own choice, or `abandoned` — left open and closed from elsewhere,
 * by the person on another device or by the server when a new start found it
 * stale. An abandoned ending keeps its sets but advances no plan and sends no
 * follow-ups. The summary shows `early` as a choice, not a miss.
 *
 * TWO enums, on purpose. This one is what `POST …/end` ACCEPTS, and widening
 * an input is safe once the server that accepts it is live. The summary's is
 * {@link SUMMARY_END_REASONS}, and it never gains `abandoned`: every installed
 * app decodes the summary with its own copy of that enum, a value the copy
 * lacks fails the decode, and the outbox then quarantines an end that
 * succeeded (`D172`). An abandoned session's summary answers `endReason: null`
 * with `abandoned: true` beside it, which an old app strips unread.
 */
export const SESSION_END_REASONS = ["complete", "early", "abandoned"];
export const sessionEndReason = z.enum(SESSION_END_REASONS);
/** The end reasons a {@link sessionSummary} may carry — frozen at two, see
 *  {@link SESSION_END_REASONS}. */
export const SUMMARY_END_REASONS = ["complete", "early"];
export const summaryEndReason = z.enum(SUMMARY_END_REASONS);
export const sessionSummary = z.object({
    /** Present, and `true`, only when the session ended `abandoned` — the one
     *  reason `endReason` cannot carry (see {@link SESSION_END_REASONS}). An
     *  app that predates it strips the key and reads a plain `null` ending. */
    abandoned: z.literal(true).optional(),
    /**
     * `nextSessionChanges` with parameters (`D144`). Both are sent: an app that
     * predates this field still renders the bare keys, and an app that reads
     * this one ignores those.
     */
    changes: z.array(sessionSummaryChange).optional(),
    durationMinutes: z.int().nonnegative(),
    /** What the ending said (`D42`); `null` when it said nothing — an old
     *  client, or a session ended before the field — and `null` for an
     *  `abandoned` ending, which {@link SUMMARY_END_REASONS} cannot carry.
     *  Optional for the deploy window only. */
    endReason: summaryEndReason.nullable().optional(),
    exerciseCount: z.int().nonnegative(),
    /** `MILESTONES` reached by THIS session (`stats.ts`) — i18n keys. */
    milestonesReached: z.array(z.string().min(1)).optional(),
    /** `ACHIEVEMENTS` reached by THIS session (`lifetime.ts`, `D204`) — ids, as
     *  strings so a newer one decodes; the app names those it has copy for. */
    achievementsReached: z.array(z.string().min(1)).optional(),
    /** "Treino nº N": this session's place among the person's workouts, oldest
     *  first, abandoned ones left out (`D204`). `null` for an abandoned one. */
    workoutNumber: z.int().positive().nullable().optional(),
    /** The muscles this session worked (`D203`), in `muscleId` order. */
    muscles: z.array(summaryMuscle).optional(),
    /** `null` when no earlier workout trained the same muscles (`D204`). */
    matched: summaryMatched.nullable().optional(),
    /** Exercises whose best set today beat every previous one. */
    newMaxExerciseIds: z.array(z.string().min(1)),
    /**
     * Why the NEXT session of this plan session looks different — the
     * progression engine's `reasonKey`s (§7.4, `progression.ts`), deduplicated,
     * for the app to resolve as i18n keys. Empty when nothing changed, when the
     * bout was unplanned, or when this end was a double tap.
     */
    nextSessionChanges: z.array(z.string().min(1)),
    /** The plan session due next, or `null` when the plan is finished or absent. */
    nextPlanSessionId: z.string().nullable(),
    /** `newMaxExerciseIds` with the one fact a celebration needs: whether this
     *  was a record BEATEN or the first lift there was nothing to beat. */
    personalRecords: z.array(personalRecord).optional(),
    previousVolumeKg: z.number().nullable(),
    /**
     * The bout `previousVolumeKg` was read from — the last real one of this
     * plan session (`D255`). `null` exactly when `previousVolumeKg` is; absent
     * from an older server. The app dates the comparison with it, and draws
     * one card rather than two when `matched` names the same session.
     */
    previousSession: z
        .object({
        sessionId: z.string().min(1),
        startedAt: z.iso.datetime(),
    })
        .nullable()
        .optional(),
    /** Metres covered by every distance set of the session (`D151`). */
    totalDistanceMetres: z.number().nonnegative().optional(),
    /** Seconds held across every timed set — a session of holds has a volume of
     *  zero kilograms, and this is what it reports beside it (`D151`). */
    totalHoldSeconds: z.int().nonnegative().optional(),
    totalVolumeKg: z.number().nonnegative(),
});
/** A sanity bound on {@link sessionEndInput}'s `pausedSeconds`: a month. The
 *  real bound is the session's span, which only the server knows (`D245`). */
export const MAX_PAUSED_SECONDS = 31 * 24 * 60 * 60;
/**
 * `POST /me/sessions/:id/end`. `{}` stays valid, so an old client keeps
 * working. Ending is idempotent: a replay answers `changed: false` and moves
 * neither `endedAt` nor `endReason`.
 */
export const sessionEndInput = z.strictObject({
    /** The client's clock at the end (`D20`), for an end replayed after the
     *  fact. Omitted = the server's now. Never in the future (422); one earlier
     *  than the session's last set is raised to that set's `performedAt`. */
    endedAt: z.iso.datetime().optional(),
    /** `D42`. Omitted = not said, stored as `null`. */
    endReason: sessionEndReason.optional(),
    /**
     * The seconds the person had the session's clock PAUSED (`D245`), in
     * total — never a delta, so a replayed end cannot add it twice. Recorded
     * only by the ending that changed the session, like `endReason`; the
     * server clamps it to the session's own span. Omitted = no pause, which
     * is every app that predates it. The server deploys first: an older one
     * refuses the key with 422.
     */
    pausedSeconds: z.int().nonnegative().max(MAX_PAUSED_SECONDS).optional(),
});
export const sessionEndOutput = z.object({
    /** `false` when the session was already ended. Ending twice is not an error
     *  — a double tap must not move `ended_at` — and the flag is how the screen
     *  knows whether this is the tap that produced the summary. */
    changed: z.boolean(),
    endedAt: z.iso.datetime(),
    summary: sessionSummary,
});
/** How many exercises one history request may ask about at once. D1 bills rows
 *  read (`T-6`) and this read is a chart, not an export. */
export const MAX_HISTORY_EXERCISE_IDS = 20;
/**
 * `GET /me/exercise-history?exerciseIds=a,b&from&to` — the chart behind
 * progress against the person (`D104`).
 *
 * Comma-separated and not repeated `exerciseIds=` parameters: one string is
 * one thing to build and one thing to parse, and every runtime this contract
 * touches disagrees about repeated keys somewhere.
 */
export const exerciseHistoryQuery = z.object({
    exerciseIds: z
        .string()
        .min(1)
        .transform((raw) => raw.split(","))
        .pipe(z.array(z.string().min(1).max(64)).max(MAX_HISTORY_EXERCISE_IDS))
        .optional(),
    from: calendarDay.optional(),
    to: calendarDay.optional(),
});
export const exerciseHistoryOutput = z.object({
    /** Oldest first — chart order. Each set carries the session it came from so
     *  a point can link back to the day without a second read. */
    sets: z.array(z.object({
        ...workoutSetView.shape,
        sessionId: z.string().min(1),
    })),
});
/** The one query string builder, so no client joins the ids itself. */
export function toExerciseHistoryParams(query) {
    const params = {};
    if (query.exerciseIds !== undefined && query.exerciseIds.length > 0) {
        params.exerciseIds = query.exerciseIds.join(",");
    }
    if (query.from !== undefined) {
        params.from = query.from;
    }
    if (query.to !== undefined) {
        params.to = query.to;
    }
    return params;
}
//# sourceMappingURL=workout.js.map